import { copy } from '../copy.js';
import { addDays, compareDates, dateLong, monthFromKey, monthName, partsOf } from '../dates.js';
import { earliestDay, earliestMonthKey, entriesOf, isYou, memberOf, meOf, todayMonthKey, todayOf } from '../state/lookup.js';
import type { AppState } from '../state/types.js';
import { color } from '../tokens.js';

/** The title row, the name under the date and the weekday row, ported from `headerVals` (02 §2.2–2.3). */

export interface ArrowVals { readonly off: boolean; readonly op: number; readonly aria: string }
export interface HeaderVals {
  /** Calendar, day, post and composer have a title row; profiles do not. */
  readonly show: boolean;
  readonly op: number;
  readonly title: string;
  /**
   * The greyed year after the title: on the calendar, and (owner, 4 October 2026) on a day and its posts.
   * The prototype shows it on the calendar only.
   */
  readonly year: number | null;
  /** The name under the date (D1): shown on a post and in the composer. */
  readonly big: { readonly mounted: boolean; readonly text: string; readonly handle: string; readonly color: string; readonly op: number; readonly tf: string };
  readonly prev: ArrowVals;
  readonly next: ArrowVals;
  /** What the arrows step: months on the calendar, days in a day or post, nothing in the composer. */
  readonly arrows: 'month' | 'day' | 'none';
  readonly wdOp: number;
  readonly wdTr: string;
  /** The hovered member's name in the weekday row (day view). */
  readonly name: { readonly text: string; readonly color: string; readonly x: number } | null;
  /** The composer's status in the weekday row. */
  readonly status: { readonly text: string; readonly color: string } | null;
  readonly home: { readonly aria: string; readonly current: boolean };
}

/** What `composeState` says about saving, needed here for the status line. */
export interface ComposeFlags { readonly saving: boolean; readonly failed: boolean }

export function headerVals(st: AppState, cs: ComposeFlags | null): HeaderVals {
  const view = st.view, inDay = view === 'day' || view === 'person', comp = view === 'compose';
  const personShown = view === 'person' && !!st.person && st.pp !== 'out' && st.pp !== 'in-pre', two = personShown || comp;
  const today = todayOf(st), homeMonth = monthName(monthFromKey(todayMonthKey(st)));
  const ym = monthFromKey(st.month);

  const bm = comp ? meOf(st) : memberOf(st, st.person);
  // The author's @handle next to the name (owner, 4 October 2026).
  const big = { mounted: comp || (view === 'person' && !!st.person), text: bm.name, handle: bm.handle ? `@${bm.handle}` : '', color: bm.color, op: two && !st.lv ? 1 : 0, tf: two ? 'none' : 'translateY(-6px)' };

  let prev: ArrowVals, next: ArrowVals, arrows: HeaderVals['arrows'];
  if (inDay) {
    const first = compareDates(st.date, earliestDay(st)) <= 0, last = compareDates(st.date, today) >= 0;
    prev = { off: first || !!st.cd, op: first ? 0.3 : 1, aria: first ? copy.previousDay() : copy.previousDay(dateLong(addDays(st.date, -1))) };
    next = { off: last || !!st.cd, op: last ? 0.3 : 1, aria: last ? copy.nextDay() : copy.nextDay(dateLong(addDays(st.date, 1))) };
    arrows = 'day';
  } else if (comp) {
    prev = { off: true, op: 0.3, aria: copy.previousDay() };
    next = { off: true, op: 0.3, aria: copy.nextDay() };
    arrows = 'none';
  } else {
    const first = st.month === earliestMonthKey(st), last = st.month === todayMonthKey(st);
    prev = { off: first, op: first ? 0.3 : 1, aria: copy.previousMonth };
    next = { off: last, op: last ? 0.3 : 1, aria: copy.nextMonth };
    arrows = 'month';
  }

  const ph = st.cd;
  let name: HeaderVals['name'] = null;
  if (view === 'day' && !st.cd && st.hoverP) {
    const index = entriesOf(st, st.date).findIndex((entry) => entry.memberId === st.hoverP);
    if (index >= 0) {
      const m = memberOf(st, st.hoverP);
      name = { text: isYou(st, st.hoverP) ? copy.you : m.name, color: m.color, x: (index % 7) * 82 + 34 - 80 };
    }
  }
  const statusText = !comp || !cs ? '' : cs.saving ? copy.status.saving : cs.failed ? copy.status.draftKept : '';
  const atHome = view === 'cal' && st.month === todayMonthKey(st);

  return {
    show: view === 'cal' || inDay || comp,
    op: st.lv ? 0 : 1,
    title: inDay ? dateLong(st.date) : comp ? dateLong(st.kDate) : monthName(ym),
    year: inDay ? partsOf(st.date).year : !comp ? ym.year : null,
    big, prev, next, arrows,
    wdOp: (view === 'cal' && !ph) || ph === 'in-pre' || ph === 'out' ? 1 : 0,
    wdTr: ph === 'in' ? 'opacity 200ms ease' : ph === 'out' ? 'opacity 300ms ease 260ms' : 'none',
    name,
    status: statusText ? { text: statusText, color: cs?.failed ? color.danger : color.textFaint } : null,
    home: { aria: atHome ? copy.home.here(homeMonth) : copy.home.back(homeMonth), current: atHome },
  };
}
